import React, { useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { View, ActivityIndicator, DeviceEventEmitter } from "react-native";
import tw from "twrnc";
import Text from "./AppText";
import Button, { ButtonRow, CopyButton } from "@/components/settings/Button";
import Callout from "@/components/settings/Callout";
import { TextField, TextInputBox, Hint } from "@/components/settings/Field";
import {
  UnsavedChanges,
  useSaveStatusReporter,
} from "@/components/settings/SaveStatus";
import { RowGroup, ToggleRow } from "@/components/settings/SettingRow";
import {
  SettingsPage,
  SettingsSection,
} from "@/components/settings/SettingsPage";
import { colors, radius, text } from "@/components/settings/theme";
import { donationRequest, resolvePledgeOrganization } from "../http/donations";
import { useNavigation } from "@react-navigation/native";
import { isRoom, roomLabelOf, roomNounOf } from "../rooms/rooms";
import {
  clearFundraiserIntent,
  fundraiserPrefill,
  readFundraiserIntent,
} from "../donations/fundraiserIntent";
import { makeUrl } from "../globalUtils";
import { t, tc } from "@/localization";

const parseOrganizations = (value) => {
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};
// Room nouns are stored in English (components/rooms/rooms.js); these are the
// lowercase words for the sentence below.
const ROOM_NOUN_WORDS = {
  Room: () => tc("room noun", "room"),
  Stage: () => tc("room noun", "stage"),
  Track: () => tc("room noun", "track"),
  Court: () => tc("room noun", "court"),
  Field: () => tc("room noun", "field"),
  Hall: () => tc("room noun", "hall"),
  Camera: () => tc("room noun", "camera"),
};
// Rooms (components/rooms) use their main event's fundraiser: one Pledge
// fundraiser maps to one event, so a room has none of its own to set up.
function RoomDonationsNotice({ event }) {
  const navigation = useNavigation();
  const roomNoun = roomNounOf(event);
  const noun = ROOM_NOUN_WORDS[roomNoun]?.() ?? roomNoun.toLowerCase();
  return (
    <SettingsPage title={t("Fundraising")} help="fundraising.organizations">
      <Callout
        tone="info"
        title={t("Every {noun} shares one fundraiser", { noun })}
        action={
          <Button
            text={t("Open the main event's fundraising")}
            size="md"
            fullWidth={false}
            onPress={() =>
              navigation.navigate("EventDonations", {
                slug: event.room_of,
                key: event.room_of,
              })
            }
          />
        }
      >
        {t(
          "The donate button (and the stream overlay, if it's on) appear in {room} too, and donations count toward the same goal.",
          { room: roomLabelOf(event) }
        )}
      </Callout>
    </SettingsPage>
  );
}

export default function EventDonationsForm(props) {
  if (isRoom(props.event)) {
    return <RoomDonationsNotice event={props.event} />;
  }
  return <EventDonationsSettings {...props} />;
}

function EventDonationsSettings({ event, route }) {
  const slug = event?.slug;
  const routeParams = route?.params;
  const [selected, setSelected] = useState([]),
    [query, setQuery] = useState(""),
    [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false),
    [saving, setSaving] = useState(false),
    [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false),
    [reload, setReload] = useState(0);
  const [error, setError] = useState(""),
    [searchError, setSearchError] = useState(""),
    [notice, setNotice] = useState("");
  const [environment, setEnvironment] = useState("");
  const [widget, setWidget] = useState(true),
    [overlay, setOverlay] = useState(false),
    [guestbook, setGuestbook] = useState(false);
  const [label, setLabel] = useState(""),
    [goal, setGoal] = useState(""),
    [amount, setAmount] = useState("");
  const [prefilling, setPrefilling] = useState(false),
    [prefillNote, setPrefillNote] = useState("");
  const generation = useRef(0);
  const savedOrganizationCount = useRef(0);
  const savedSnapshot = useRef(null);
  // A fundraiser exists on Pledge (saved organizations): changes then save
  // automatically. The first save ("Turn on fundraising") stays a button: it
  // creates the Pledge fundraiser, adds the donate button to the event page
  // and, for nonprofits, can unlock the event.
  const [live, setLive] = useState(false);
  const prefilledSlug = useRef(null);
  function populate(e) {
    const organizations = parseOrganizations(e.donation_organizations);
    savedOrganizationCount.current = organizations.length;
    setSelected(organizations);
    setWidget(e.donation_widget_enabled ?? true);
    setOverlay(!!e.donation_overlay_enabled);
    setGuestbook(!!e.donation_show_in_guestbook);
    setLabel(e.donation_button_label || "");
    setGoal(e.donation_goal?.toString() || "");
    setAmount(e.donation_default_amount?.toString() || "");
    setLive(organizations.length > 0);
    savedSnapshot.current = JSON.stringify([
      organizations.map((o) => o.id),
      e.donation_widget_enabled ?? true,
      !!e.donation_overlay_enabled,
      !!e.donation_show_in_guestbook,
      e.donation_button_label || "",
      e.donation_goal?.toString() || "",
      e.donation_default_amount?.toString() || "",
    ]);
  }
  useEffect(() => {
    let active = true;
    if (!slug) {
      setLoading(false);
      setLoaded(false);
      setError(t("Choose an event to set up fundraising."));
      return;
    }
    setLoading(true);
    setError("");
    setLoaded(false);
    donationRequest(`settings?slug=${encodeURIComponent(slug)}`)
      .then((data) => {
        if (active) {
          populate(data.event || {});
          setEnvironment(data.environment);
          setLoaded(true);
        }
      })
      .catch((e) => active && setError(e.message))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [slug, reload]);

  // Preselect the organization a nonprofit chose on eventlive.pro/nonprofit
  // (or passed as ?pledge_org_id= / ?pledge_ein=). Nothing is saved until the
  // broadcaster reviews it and presses Save.
  useEffect(() => {
    if (!loaded || !slug || prefilledSlug.current === slug) return;
    prefilledSlug.current = slug;
    let active = true;
    (async () => {
      const intent = await readFundraiserIntent();
      const prefill = fundraiserPrefill({ params: routeParams, intent, slug });
      if (!active || !prefill || savedOrganizationCount.current > 0) return;
      const orgLabel =
        prefill.name || (prefill.ein ? `EIN ${prefill.ein}` : "");
      if (prefill.id || prefill.ein) {
        setPrefilling(true);
        let organization = null;
        try {
          organization = await resolvePledgeOrganization(prefill);
        } catch (e) {
          organization = null;
        }
        if (!active) return;
        setPrefilling(false);
        if (organization) {
          setSelected((current) =>
            current.some((o) => o.id === organization.id)
              ? current
              : [...current, organization]
          );
          setPrefillNote(
            t(
              "We added {name}. Check the details, then press Save to turn on donations.",
              { name: organization.name }
            )
          );
          return;
        }
      }
      if (!orgLabel) return;
      setQuery(prefill.name || prefill.ein);
      setPrefillNote(
        prefill.ein && !prefill.id
          ? t(
              "We couldn't find {organization} yet. Newly added organizations can take a few days to appear, so search again later.",
              { organization: orgLabel }
            )
          : t(
              "Select {organization} from the search results below, then press Save.",
              { organization: orgLabel }
            )
      );
    })();
    return () => {
      active = false;
    };
  }, [loaded, slug]);

  useEffect(() => {
    const seq = ++generation.current;
    setSearchError("");
    if (query.trim().length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = setTimeout(
      () =>
        donationRequest(`organizations?q=${encodeURIComponent(query.trim())}`)
          .then((data) => {
            if (seq === generation.current)
              setResults(
                Array.isArray(data.organizations) ? data.organizations : []
              );
          })
          .catch((e) => {
            if (seq === generation.current) {
              setResults([]);
              setSearchError(e.message);
            }
          })
          .finally(() => {
            if (seq === generation.current) setSearching(false);
          }),
      350
    );
    return () => {
      clearTimeout(timer);
      generation.current++;
    };
  }, [query]);
  const values = { selected, widget, overlay, guestbook, label, goal, amount };
  const snapshotOf = (v) =>
    JSON.stringify([
      v.selected.map((o) => o.id),
      v.widget,
      v.overlay,
      v.guestbook,
      v.label,
      v.goal,
      v.amount,
    ]);
  const latest = useRef(values);
  latest.current = values;
  const inFlight = useRef(false);
  const again = useRef(false);
  const report = useSaveStatusReporter("fundraising");
  const needsOrganization = (v) =>
    !v.selected.length && (v.widget || v.overlay || v.guestbook);

  // One save at a time; changes made during a save are saved right after it.
  async function save({ first = false } = {}) {
    if (inFlight.current) {
      again.current = true;
      return;
    }
    const v = latest.current;
    const snapshot = snapshotOf(v);
    if (!first && snapshot === savedSnapshot.current) {
      report?.(null);
      return;
    }
    if (needsOrganization(v)) {
      report?.({
        state: "invalid",
        message: t("choose an organization"),
        at: Date.now(),
      });
      return;
    }
    inFlight.current = true;
    setSaving(true);
    setError("");
    report?.({ state: "saving", at: Date.now() });
    try {
      const data = await donationRequest("settings", {
        slug,
        organization_ids: v.selected.map((o) => o.id),
        donation_widget_enabled: v.widget,
        donation_overlay_enabled: v.overlay,
        donation_show_in_guestbook: v.guestbook,
        donation_button_label: v.label,
        donation_goal: v.goal || null,
        donation_default_amount: v.amount || null,
      });
      if (snapshotOf(latest.current) === snapshot) {
        // Nothing changed during the save: show what the server stored.
        populate(data.event || {});
      } else {
        savedSnapshot.current = snapshot;
        setLive(v.selected.length > 0);
      }
      if (first) {
        setPrefillNote("");
        setNotice(data.notice || t("Fundraiser saved."));
        const intent = await readFundraiserIntent();
        if (intent && intent.slug === slug) await clearFundraiserIntent();
      }
      DeviceEventEmitter.emit("refresh_event");
      report?.({ state: "saved", at: Date.now() });
    } catch (e) {
      setError(e.message);
      report?.({
        state: "error",
        message: e.message,
        retry: () => save(),
        at: Date.now(),
      });
    } finally {
      inFlight.current = false;
      setSaving(false);
      if (again.current) {
        again.current = false;
        save();
      }
    }
  }
  const autoSave = useDebouncedCallback(() => save(), 1000);
  const valuesKey = snapshotOf(values);
  useEffect(() => {
    if (!live || !loaded || valuesKey === savedSnapshot.current) return;
    report?.({ state: "pending", at: Date.now() });
    autoSave();
  }, [live, loaded, valuesKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Leaving the screen: send what's pending.
  useEffect(() => () => autoSave.flush(), [autoSave]);
  const available = results.filter((o) => !selected.some((s) => s.id === o.id));
  const dirty =
    loaded &&
    savedSnapshot.current !== null &&
    valuesKey !== savedSnapshot.current;
  // Inputs are locked only during the first save; later saves run in the
  // background while you keep editing.
  const locked = saving && !live;
  const orgCard = (org, { selectedCard, onPress }) => (
    <View
      key={org.id}
      style={[
        tw`p-3 mb-2`,
        {
          borderWidth: 1,
          borderRadius: radius.control,
          borderColor: selectedCard ? colors.brandBorder : colors.border,
          backgroundColor: selectedCard ? colors.brandTint : colors.white,
        },
      ]}
    >
      <View style={tw`flex-row items-start`}>
        <View style={tw`flex-1 pr-2`}>
          <Text style={text.bodyStrong}>{org.name}</Text>
          <Text style={[text.hint, tw`mt-0.5`]}>
            {org.ein ? `EIN ${org.ein}` : t("EIN unavailable")}
            {org.address ? ` · ${org.address}` : ""}
          </Text>
          {!selectedCard && !!org.mission && (
            <Text numberOfLines={2} style={[text.hint, tw`mt-1`]}>
              {org.mission}
            </Text>
          )}
        </View>
        <Button
          text={selectedCard ? t("Remove") : t("Add")}
          variant={selectedCard ? "danger-text" : "secondary"}
          icon={selectedCard ? undefined : "add"}
          size="sm"
          fullWidth={false}
          disabled={locked || (!selectedCard && selected.length >= 20)}
          accessibilityLabel={
            selectedCard
              ? t("Remove {name}", { name: org.name })
              : t("Add {name}", { name: org.name })
          }
          onPress={onPress}
        />
      </View>
    </View>
  );
  return (
    <SettingsPage
      title={t("Fundraising")}
      description={t(
        "Bring people together for a cause. Choose one or more organizations to receive donations through Pledge."
      )}
    >
      <UnsavedChanges dirty={!live && !!dirty} />
      {environment === "staging" && (
        <Callout tone="warning" style={tw`mb-4`}>
          {t(
            "Test fundraiser · Donations in this environment are for testing."
          )}
        </Callout>
      )}
      {!!error && (
        <Callout tone="danger" style={tw`mb-4`}>
          {error}
        </Callout>
      )}
      {loading ? (
        <ActivityIndicator accessibilityLabel={t("Loading fundraiser")} />
      ) : !loaded ? (
        <Button
          text={t("Try again")}
          icon="refresh"
          size="md"
          fullWidth={false}
          onPress={() => setReload(reload + 1)}
        />
      ) : (
        <>
          <SettingsSection
            first
            title={t("Organizations")}
            help="fundraising.organizations"
          >
            {!!prefillNote && (
              <Callout tone="neutral" live style={tw`mb-3`}>
                {prefillNote}
              </Callout>
            )}
            {selected.map((org) =>
              orgCard(org, {
                selectedCard: true,
                onPress: () =>
                  setSelected(selected.filter((o) => o.id !== org.id)),
              })
            )}
            <TextInputBox
              accessibilityLabel={t("Search organizations by name or EIN")}
              placeholder={t("Search by name or EIN")}
              value={query}
              onChangeText={setQuery}
              autoCorrect={false}
              disabled={locked}
              actions={
                query
                  ? [
                      {
                        icon: "close-circle",
                        label: t("Clear search"),
                        onPress: () => setQuery(""),
                      },
                    ]
                  : undefined
              }
            />
            <Hint>
              {t(
                "Check the EIN and address to make sure you have the right organization."
              )}
            </Hint>
            {(prefilling || searching) && (
              <ActivityIndicator
                accessibilityLabel={
                  prefilling
                    ? t("Finding your organization")
                    : t("Searching organizations")
                }
                style={tw`my-3`}
              />
            )}
            {!!searchError && (
              <Callout tone="danger" style={tw`mt-2`}>
                {searchError}
              </Callout>
            )}
            {!searching &&
              !searchError &&
              query.trim().length >= 2 &&
              !available.length && (
                <Text style={[text.hint, tw`mt-3`]}>
                  {t(
                    "No more matches. Try the organization’s full name or EIN."
                  )}
                </Text>
              )}
            {!searching && available.length > 0 && (
              <View style={tw`mt-3`}>
                {available.map((org) =>
                  orgCard(org, {
                    selectedCard: false,
                    onPress: () => {
                      setSelected([...selected, org]);
                      setQuery("");
                      setPrefillNote("");
                    },
                  })
                )}
              </View>
            )}
          </SettingsSection>

          <SettingsSection title={t("Donation options")}>
            <RowGroup>
              <ToggleRow
                label={t("Show donations on the event page")}
                help="fundraising.widget"
                value={widget}
                onValueChange={setWidget}
                disabled={locked}
              />
              <ToggleRow
                label={t("Show the live donation tracker on your stream")}
                description={t(
                  "Shows the amount raised, new donations and a Scan to donate QR code. Changes apply the next time you go live."
                )}
                help="fundraising.overlay"
                value={overlay}
                onValueChange={setOverlay}
                disabled={locked}
              />
              <ToggleRow
                label={t("Show donations in the guestbook")}
                description={t(
                  "Confirmed donations appear in the guestbook or chat when messaging is on. Anonymous donors stay anonymous."
                )}
                help="fundraising.guestbook"
                value={guestbook}
                onValueChange={setGuestbook}
                disabled={locked}
              />
            </RowGroup>
          </SettingsSection>

          <SettingsSection
            title={t("Button and goal")}
            help="fundraising.options"
          >
            <TextField
              label={t("Button label")}
              value={label}
              onChangeText={setLabel}
              placeholder={t("Donate")}
              maxLength={50}
              disabled={locked}
            />
            <TextField
              label={t("Fundraising goal (USD)")}
              optional
              value={goal}
              onChangeText={setGoal}
              placeholder={t("e.g. 5000")}
              keyboardType="decimal-pad"
              maxLength={12}
              disabled={locked}
            />
            <TextField
              label={t("Suggested donation (USD)")}
              value={amount}
              onChangeText={setAmount}
              placeholder="100"
              keyboardType="decimal-pad"
              maxLength={12}
              disabled={locked}
              last
            />
          </SettingsSection>

          {live && !!event && (
            <SettingsSection
              title={t("Share and manage")}
              help="fundraising.pledge"
            >
              <Text style={text.bodyStrong}>{t("Donate link")}</Text>
              <Text style={[text.body, tw`mt-1`]} selectable>
                {`${makeUrl(event).replace(/^https?:\/\//, "")}?donate`}
              </Text>
              <Hint>
                {t(
                  "Opens your event page with the donation form, even for people who haven't registered or bought a ticket. The QR code on your stream opens it too."
                )}
              </Hint>
              <ButtonRow align="start" style={tw`mt-3 mb-4`}>
                <CopyButton
                  value={`${makeUrl(event)}?donate`}
                  text={t("Copy donate link")}
                  toast={t("Donate link copied")}
                  toastDetail={`${makeUrl(event)}?donate`}
                  size="md"
                  fullWidth={false}
                />
              </ButtonRow>
              <Text style={text.bodyStrong}>{t("On Pledge")}</Text>
              <Hint>
                {t(
                  "You're an organizer of this fundraiser on Pledge. Create a free Pledge account (or sign in) at pledge.to with your EventLive account's email to see donor details, customize the campaign page and add a text-to-donate keyword. Changes you save here update the title, organizations and goal on Pledge."
                )}
              </Hint>
            </SettingsSection>
          )}

          {live ? (
            <>
              {needsOrganization(values) && (
                <Callout
                  tone="warning"
                  style={tw`mt-6`}
                  action={
                    <Button
                      text={t("Turn off fundraising")}
                      variant="secondary"
                      size="sm"
                      fullWidth={false}
                      onPress={() => {
                        setWidget(false);
                        setOverlay(false);
                        setGuestbook(false);
                      }}
                    />
                  }
                >
                  {t(
                    "Choose an organization to keep fundraising, or turn it off."
                  )}
                </Callout>
              )}
              {!!notice && (
                <Callout tone="success" live style={tw`mt-6`}>
                  {notice}
                </Callout>
              )}
            </>
          ) : (
            <View style={tw`mt-8`}>
              <Button
                text={t("Turn on fundraising")}
                icon="heart-outline"
                variant="primary"
                loading={saving}
                disabled={prefilling || !selected.length}
                onPress={() => save({ first: true })}
              />
              <Hint style={tw`text-center`}>
                {selected.length
                  ? t(
                      "Creates your fundraiser on Pledge, with you as its organizer (your account email). After that, changes save automatically."
                    )
                  : t("Choose an organization first.")}
              </Hint>
            </View>
          )}
        </>
      )}
    </SettingsPage>
  );
}
